import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { MODALS_NS } from './i18n';

type ModalType = 'loading' | 'error' | 'confirmation';

interface StatusModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  type: ModalType;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

export const StatusModal = ({
  open,
  onClose,
  onConfirm,
  type,
  message,
  title,
  confirmText,
  cancelText,
}: StatusModalProps) => {
  const { t } = useTranslation(MODALS_NS);

  const renderIcon = () => {
    switch (type) {
      case 'loading':
        return <CircularProgress size={24} />;
      case 'error':
      case 'confirmation':
        return (
          <Box
            sx={{
              borderRadius: '50%',
              border: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: getIconBackground(),
              width: 40,
              height: 40,
            }}
          >
            <InfoOutlinedIcon color={type === 'error' ? 'error' : 'primary'} />
          </Box>
        );
    }
  };

  const getIconBackground = () => {
    switch (type) {
      case 'loading':
        return 'primary.muted';
      case 'error':
        return 'error.muted';
      case 'confirmation':
        return 'primary.muted';
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{renderIcon()}</DialogTitle>
      <DialogContent
        sx={{
          minWidth: 553,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box>
            {title && (
              <Typography
                variant="h4"
                sx={{
                  fontSize: '16px',
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                {title}
              </Typography>
            )}
            <Typography>{message}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{cancelText || t('error.cancel')}</Button>
        {type === 'confirmation' && onConfirm && (
          <Button onClick={onConfirm} variant="contained" color="primary">
            {confirmText || t('error.confirm')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
